import type { ReportSchema } from '@ballerine/common';
import type { LucideIcon } from 'lucide-react';
import {
  Building,
  Check,
  Copy,
  Earth,
  Globe,
  Hash,
  Mail,
  MapPin,
  Phone,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import type z from 'zod';

import { ctw } from '@/common';

type MerchantDetailsContentProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'] | null;
  className?: string;
};

const CopyButton = React.memo<{ textToCopy: string; className?: string }>(
  ({ textToCopy, className }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard?.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
    };

    return (
      <button
        onClick={handleCopy}
        className={ctw('text-gray-400 hover:text-gray-600 transition-colors', className)}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      </button>
    );
  },
);

CopyButton.displayName = 'CopyButton';

export const DataRow: React.FC<{
  label: string;
  Icon: LucideIcon;
  value: React.ReactNode;
  copyable?: boolean;
  copyValue?: string;
  emptyLabel?: string;
}> = ({ label, Icon, value, copyable = false, copyValue, emptyLabel = 'Not Detected' }) => {
  const hasValue =
    value !== null &&
    value !== undefined &&
    !(typeof value === 'string' && value.trim().length === 0);

  return (
    <div className="grid grid-cols-2 items-center">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-500" />
        <p className="mb-1 text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className={ctw('text-sm', hasValue ? 'text-gray-900 truncate' : 'text-slate-400')}>
          {hasValue ? value : emptyLabel}
        </div>
        {hasValue && copyable && copyValue && <CopyButton textToCopy={copyValue} />}
      </div>
    </div>
  );
};

export const DFMerchantDetailsContent: React.FC<MerchantDetailsContentProps> = ({
  merchantDetails,
  className,
}) => {
  return (
    <div className={ctw('space-y-4', className)}>
      <DataRow
        label="Company Name"
        Icon={Building}
        value={merchantDetails?.companyName}
        copyable={!!merchantDetails?.companyName}
        copyValue={merchantDetails?.companyName || ''}
      />

      <DataRow
        label="Registration Number"
        Icon={Hash}
        value={merchantDetails?.registrationNumber}
        copyable={!!merchantDetails?.registrationNumber}
        copyValue={merchantDetails?.registrationNumber || ''}
      />

      <DataRow
        label="Phone Number"
        Icon={Phone}
        value={merchantDetails?.phoneNumber}
        copyable={!!merchantDetails?.phoneNumber}
        copyValue={merchantDetails?.phoneNumber || ''}
      />

      <DataRow
        label="Email"
        Icon={Mail}
        value={merchantDetails?.email}
        copyable={!!merchantDetails?.email}
        copyValue={merchantDetails?.email || ''}
      />

      <DataRow
        label="Country"
        Icon={Earth}
        value={merchantDetails?.country}
        copyable={!!merchantDetails?.country}
        copyValue={merchantDetails?.country || ''}
      />

      <DataRow
        label="Address"
        Icon={MapPin}
        value={merchantDetails?.address}
        copyable={!!merchantDetails?.address}
        copyValue={merchantDetails?.address || ''}
      />

      <DataRow
        label="Website"
        Icon={Globe}
        value={
          merchantDetails?.website ? (
            <a
              href={merchantDetails.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {merchantDetails.website}
            </a>
          ) : null
        }
        copyable={!!merchantDetails?.website}
        copyValue={merchantDetails?.website || ''}
      />

      <DataRow
        label="Associated Persons"
        Icon={Users}
        value={
          merchantDetails?.associatedPersons &&
          Array.isArray(merchantDetails.associatedPersons) &&
          merchantDetails.associatedPersons.length > 0 ? (
            <div className="space-y-1">
              {merchantDetails.associatedPersons.map((person: string, index: number) => (
                <div key={index} className="text-sm text-gray-900">
                  {person}
                </div>
              ))}
            </div>
          ) : null
        }
        copyable={!!merchantDetails?.associatedPersons?.length}
        copyValue={merchantDetails?.associatedPersons?.join(', ') || ''}
      />
    </div>
  );
};
